import React, { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import api from "../../api/api";
import { getPracticeId } from "../../config/practice";
import { statusMeta } from "../signinmodel";
import "./statuspopup.css";

// Phone / scheduling outcomes; everything else the API returns is a check-in
// (in-clinic) status.
const CONFIRMATION_STATUSES = [
  "No Update",
  "Tele - Confirmed",
  "In-Office - Confirmed",
  "Tele - left VM",
  "In-Office - left VM",
  "In-office - Checked In",
  "Awaiting Check In",
  "Rescheduled",
  "No Show",
  "Cancelled",
  "ACTIVE",
];

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12.5 10 17 19 7.5" />
  </svg>
);
const Close = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

const StatusPopup = ({ isOpen, onClose, updateStatus }) => {
  const [selectedOption, setSelectedOption] = useState(null); // single selection
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const practiseid = getPracticeId();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/v1/${practiseid}/appointments/status-types`, {
        appointment_types: ["check-in", "visit confirmation"],
      });
      setStatusOptions(response.status === 200 ? response.data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleApplyClick = () => {
    updateStatus([selectedOption]);
    onClose();
  };

  if (!isOpen) return null;

  const values = statusOptions.map((item) => item.value);
  const confirmation = values.filter((v) => CONFIRMATION_STATUSES.includes(v));
  const checkin = values.filter((v) => !CONFIRMATION_STATUSES.includes(v));
  // The sheet always offers Ready for Checkout, even when the status list
  // omits it.
  if (!values.includes("Ready for Checkout")) checkin.push("Ready for Checkout");

  const option = (value) => {
    const on = selectedOption === value;
    return (
      <button
        type="button"
        key={value}
        role="radio"
        aria-checked={on}
        className={`bmst-opt tone-${statusMeta(value).tone}${on ? " on" : ""}`}
        onClick={() => setSelectedOption(value)}
      >
        <span className="bmst-dot" />
        <span className="bmst-label">{value}</span>
        {on && <Check />}
      </button>
    );
  };

  return (
    <div className="bmst-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bmst" role="dialog" aria-modal="true" aria-labelledby="bmst-title">
        <div className="bmst-head">
          <div>
            <div id="bmst-title" className="bmst-title">Update status</div>
            <div className="bmst-sub">Pick the patient's visit status</div>
          </div>
          <button type="button" className="bmst-x" onClick={onClose} aria-label="Close">
            <Close />
          </button>
        </div>

        <div className="bmst-body">
          {loading ? (
            <div className="bmst-grid">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={38} sx={{ borderRadius: "10px" }} />
              ))}
            </div>
          ) : (
            <>
              {confirmation.length > 0 && (
                <>
                  <div className="bmst-group">Visit confirmation</div>
                  <div className="bmst-grid" role="radiogroup" aria-label="Visit confirmation">
                    {confirmation.map(option)}
                  </div>
                </>
              )}
              <div className="bmst-group">Check-in</div>
              <div className="bmst-grid" role="radiogroup" aria-label="Check-in">
                {checkin.map(option)}
              </div>
            </>
          )}
        </div>

        <div className="bmst-foot">
          <button type="button" className="bmst-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bmst-btn primary"
            onClick={handleApplyClick}
            disabled={loading || !selectedOption}
          >
            Update status
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusPopup;
